/**
 * Per-tenant SFTP connection settings (host, port, username, credential ref).
 * Secrets live in `tenant_credentials`, keyed by `credentialId`.
 */
export interface SftpIntegrationSettings {
  host: string | null;
  port: number | null;
  username: string | null;
  credentialId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Writable fields for an upsert. Secret fields upsert `tenant_credentials`. */
export interface SftpIntegrationSettingsInput {
  host?: string | null;
  port?: number | null;
  username?: string | null;
  credentialId?: string | null;
  password?: string | null;
  privateKey?: string | null;
  privateKeyPassphrase?: string | null;
}

/** Secret-masked projection returned by the admin API. */
export interface SftpIntegrationSettingsView {
  host: string | null;
  port: number | null;
  username: string | null;
  credentialId: string | null;
  hasPassword: boolean;
  passwordLast4: string | null;
  hasPrivateKey: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Flow-level SFTP input: pickup folder + filename filter only. */
export const PICKUP_STRATEGIES = ['none', 'delete', 'move'] as const;
export type PickupStrategy = (typeof PICKUP_STRATEGIES)[number];

export interface SftpFlowInputConfig {
  engine: 'sftp';
  path: string;
  pickupRegex: string;
  /** What to do with the remote file after a successful pickup. Defaults to `none`. */
  pickupStrategy?: PickupStrategy;
  /** Destination folder when `pickupStrategy` is `move`. */
  pickedUpPath?: string;
  credentialId?: string;
}

export function resolvePickupStrategy(config: SftpFlowInputConfig): PickupStrategy {
  return config.pickupStrategy ?? 'none';
}

/** How delivered PDFs are named before upload. */
export const DELIVERY_STRATEGIES = ['source-suffix', 'custom'] as const;
export type DeliveryStrategy = (typeof DELIVERY_STRATEGIES)[number];

/**
 * Default pattern for `source-suffix`: reuse the pickup CSV basename and append
 * a suffix plus `{index}-{total}` (e.g. `…_VDP_0001-0006.pdf`).
 */
export const DEFAULT_SOURCE_SUFFIX_FILENAME_CONVENTION =
  '{sourceBasename}_VDP_{NNNN}-{TTTT}.pdf';

/** Flow-level SFTP output: delivery folder + naming strategy. */
export interface SftpFlowOutputConfig {
  engine: 'sftp';
  path: string;
  /**
   * Output naming strategy. `source-suffix` reuses the pickup filename stem;
   * `custom` uses `filenameConvention` with optional regex capture tokens.
   */
  deliveryStrategy?: DeliveryStrategy;
  credentialId?: string;
}

export function resolveDeliveryStrategy(config: SftpFlowOutputConfig): DeliveryStrategy {
  return config.deliveryStrategy ?? 'custom';
}

/** Resolve the pattern applied in prepare-delivery for this flow. */
export function resolveEffectiveFilenameConvention(
  strategy: DeliveryStrategy,
  filenameConvention: string,
): string {
  if (strategy === 'source-suffix') {
    const trimmed = filenameConvention.trim();
    return trimmed || DEFAULT_SOURCE_SUFFIX_FILENAME_CONVENTION;
  }
  return filenameConvention.trim();
}

/** Legacy flow JSON that still embeds connection fields (pre-tenant SFTP settings). */
export interface LegacySftpFlowInputConfig extends SftpFlowInputConfig {
  host: string;
  port: number;
  username: string;
  credentialId: string;
}

export interface LegacySftpFlowOutputConfig extends SftpFlowOutputConfig {
  host: string;
  port: number;
  username: string;
  credentialId: string;
}

/** Fully resolved endpoint passed to the SFTP sidecar. */
export interface ResolvedSftpEndpoint {
  host: string;
  port: number;
  username: string;
  path: string;
  credentialId: string;
  pickupRegex?: string;
}

export function isLegacySftpInputConfig(
  config: SftpFlowInputConfig | LegacySftpFlowInputConfig,
): config is LegacySftpFlowInputConfig {
  return typeof (config as LegacySftpFlowInputConfig).host === 'string';
}

export function isLegacySftpOutputConfig(
  config: SftpFlowOutputConfig | LegacySftpFlowOutputConfig,
): config is LegacySftpFlowOutputConfig {
  return typeof (config as LegacySftpFlowOutputConfig).host === 'string';
}

/**
 * Merge tenant-level SFTP settings with flow-level path config. Legacy flow
 * JSON that still carries host/port/username wins when tenant settings are unset.
 */
export function resolveSftpInputEndpoint(
  tenant: SftpIntegrationSettings | null,
  flow: SftpFlowInputConfig | LegacySftpFlowInputConfig,
): ResolvedSftpEndpoint {
  const credentialId =
    flow.credentialId ?? tenant?.credentialId ?? 'default';

  if (isLegacySftpInputConfig(flow)) {
    return {
      host: flow.host,
      port: flow.port,
      username: flow.username,
      path: flow.path,
      credentialId,
      pickupRegex: flow.pickupRegex,
    };
  }

  if (!tenant?.host || !tenant.username) {
    throw new Error(
      'SFTP input is not configured: set host and username in tenant SFTP settings',
    );
  }

  return {
    host: tenant.host,
    port: tenant.port ?? 22,
    username: tenant.username,
    path: flow.path,
    credentialId,
    pickupRegex: flow.pickupRegex,
  };
}

export function resolveSftpOutputEndpoint(
  tenant: SftpIntegrationSettings | null,
  flow: SftpFlowOutputConfig | LegacySftpFlowOutputConfig,
): ResolvedSftpEndpoint {
  const credentialId =
    flow.credentialId ?? tenant?.credentialId ?? 'default';

  if (isLegacySftpOutputConfig(flow)) {
    return {
      host: flow.host,
      port: flow.port,
      username: flow.username,
      path: flow.path,
      credentialId,
    };
  }

  if (!tenant?.host || !tenant.username) {
    throw new Error(
      'SFTP output is not configured: set host and username in tenant SFTP settings',
    );
  }

  return {
    host: tenant.host,
    port: tenant.port ?? 22,
    username: tenant.username,
    path: flow.path,
    credentialId,
  };
}
